import { MapPin } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TILE, tilesFor, tileUrl, zoomAt } from '../../lib/tiles';

// The site sends no referrer anywhere (Helmet's default), but tile servers require one:
// OpenStreetMap answers referrer-less requests with an "Access blocked" tile. Send just the
// origin (https://canvas.kcl.cloud), never the page path.
const TILE_REFERRER = 'strict-origin';

interface Props {
  lat: number;
  lon: number;
  /** Equal for a fixed zoom; different to zoom gradually over `durationSec`. */
  zoomFrom: number;
  zoomTo: number;
  durationSec: number;
  /** When the slide appeared (ms), so a re-render mid-slide continues the zoom. */
  startedAt: number;
  template: string;
  attribution: string;
  maxZoom: number;
}

/**
 * A small slippy map with a pin in the middle. No map library: one layer of 256 px tiles
 * per whole zoom level, scaled with a CSS transform for the fractional part. Gradual zoom
 * sets the scale a short step ahead and lets a linear CSS transition run to it, so the
 * motion is smooth without re-rendering every frame (kind to TV sticks).
 */
export function TileMap({ lat, lon, zoomFrom, zoomTo, durationSec, startedAt, template, attribution, maxZoom }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const clamp = (z: number) => Math.min(maxZoom, Math.max(0, z));
  const from = clamp(zoomFrom);
  const to = clamp(zoomTo);
  const gradual = from !== to;
  // At most half a zoom level per step, so a step never magnifies tiles past ~2.8×.
  const rate = Math.abs(to - from) / Math.max(durationSec, 1);
  const stepMs = gradual ? Math.round(Math.min(1000, Math.max(150, 500 / rate))) : 0;

  const at = (offsetMs = 0) => zoomAt((Date.now() - startedAt + offsetMs) / 1000, durationSec, from, to);
  const [zoom, setZoom] = useState(() => ({ start: at(), end: at(stepMs) }));
  useEffect(() => {
    if (!gradual) {
      setZoom({ start: to, end: to });
      return;
    }
    const tick = () => {
      setZoom({ start: at(), end: at(stepMs) });
      if (Date.now() - startedAt >= durationSec * 1000) window.clearInterval(id);
    };
    const id = window.setInterval(tick, stepMs);
    tick();
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradual, from, to, stepMs, startedAt, durationSec]);

  // The most recent level whose tiles all loaded stays underneath (magnified) until the
  // current level is in, so changing level never flashes blank. Only a lower level is
  // useful there: a higher one drawn shrunk would need far too many tiles.
  const level = Math.floor(Math.min(zoom.start, zoom.end));
  const [ready, setReady] = useState<number | null>(null);
  const markReady = useCallback((l: number) => setReady((r) => (r === l ? r : l)), []);
  const underlay = ready !== null && ready < level ? ready : null;

  // Gradual zoom: fetch the next level ahead, so it's in the browser cache when needed.
  useEffect(() => {
    if (!gradual || !size.w || level + 1 > Math.max(from, to)) return;
    const imgs = tilesFor(lat, lon, level + 1, size.w, size.h, 1).map((t) => {
      const img = new Image();
      img.referrerPolicy = TILE_REFERRER;
      img.src = tileUrl(template, t);
      return img;
    });
    return () => imgs.forEach((img) => (img.src = ''));
  }, [gradual, level, from, to, lat, lon, size.w, size.h, template]);

  return (
    <div ref={box} className="relative h-full w-full overflow-hidden bg-[var(--surface-strong)]" aria-hidden="true">
      {size.w > 0 && (
        <div className="map-tiles absolute inset-0">
          {underlay !== null && (
            <Layer key={underlay} level={underlay} lat={lat} lon={lon} zoom={zoom} stepMs={stepMs} w={size.w} h={size.h} template={template} />
          )}
          <Layer
            key={level}
            level={level}
            lat={lat}
            lon={lon}
            zoom={zoom}
            stepMs={stepMs}
            w={size.w}
            h={size.h}
            template={template}
            onReady={markReady}
          />
        </div>
      )}
      <MapPin
        className="absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-full text-[var(--accent)] drop-shadow-lg"
        fill="currentColor"
        stroke="#fff"
        strokeWidth={1.5}
      />
      <span className="absolute bottom-0 right-0 rounded-tl bg-black/50 px-1.5 py-0.5 text-[0.6rem] leading-tight text-white/90">
        {attribution}
      </span>
    </div>
  );
}

interface LayerProps {
  level: number;
  lat: number;
  lon: number;
  zoom: { start: number; end: number };
  stepMs: number;
  w: number;
  h: number;
  template: string;
  /** Called with the level when every tile in view has loaded. */
  onReady?: (level: number) => void;
}

function Layer({ level, lat, lon, zoom, stepMs, w, h, template, onReady }: LayerProps) {
  const startScale = 2 ** (zoom.start - level);
  const endScale = 2 ** (zoom.end - level);
  // Tiles for the wider of the two views, so the whole step is covered.
  const coverScale = Math.min(startScale, endScale);
  const tiles = useMemo(
    () => tilesFor(lat, lon, level, w, h, coverScale),
    // Re-plan only when the covered area changes noticeably.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lat, lon, level, w, h, Math.round(coverScale * 8)],
  );

  const loaded = useRef(new Set<string>());
  const markLoaded = (img: HTMLImageElement, key: string) => {
    img.classList.add('loaded');
    loaded.current.add(key);
    if (onReady && tiles.every((t) => loaded.current.has(t.key))) onReady(level);
  };

  // The transform is set directly, so React re-renders never restart the transition.
  const el = useRef<HTMLDivElement>(null);
  const placed = useRef(false);
  useLayoutEffect(() => {
    const d = el.current;
    if (!d) return;
    if (!placed.current || !stepMs) {
      d.style.transition = 'none';
      d.style.transform = `scale(${startScale})`;
      void d.offsetWidth; // commit the start position before animating
      placed.current = true;
    }
    if (stepMs) {
      d.style.transition = `transform ${stepMs}ms linear`;
      d.style.transform = `scale(${endScale})`;
    }
  }, [startScale, endScale, stepMs]);

  return (
    <div ref={el} className="absolute left-1/2 top-1/2 will-change-transform" style={{ transformOrigin: '0 0' }}>
      {tiles.map((t) => (
        <img
          key={t.key}
          src={tileUrl(template, t)}
          referrerPolicy={TILE_REFERRER}
          alt=""
          draggable={false}
          className="map-tile absolute max-w-none"
          style={{ left: t.left, top: t.top, width: TILE, height: TILE }}
          ref={(img) => {
            if (img?.complete && img.naturalWidth) markLoaded(img, t.key);
          }}
          onLoad={(e) => markLoaded(e.currentTarget, t.key)}
        />
      ))}
    </div>
  );
}
